<template>
    <div class="dashboard">
        <div class="flex justify-between mb-8 items-center">
            <div>
                <h1 class="text-4xl tracking-widest uppercase font-bold text-gray-700">Dashboard</h1>
            </div>
        </div>
        <div class="grid grid-cols-2 gap-8">
            <div>
                <div class="alerts-list">
                    <h2 class="text-2xl tracking-widest uppercase font-bold text-gray-500">Alerts</h2>
                    <div v-for="(alert, index) in alerts" :key="index">
                        <button class="btn-danger muted rounded-md border border-red-400" type="button" @click="loadProfessionalWriting(alert.id)">
                            <span class="font-normal">{{ alert.value }}</span>
                        </button>
                    </div>
                </div>
                <h2 class="text-2xl tracking-widest uppercase font-bold text-gray-500 mt-8">Upcoming Releases</h2>
                <div class="bg-white shadow overflow-hidden sm:rounded-md mt-4">
                    <ul role="list" class="divide-y divide-gray-200 font-thin">
                        <li v-for="(upcoming, index) in releases" :key="index">
                            <router-link :to="{name: 'releases.show', params: { releaseId: upcoming.pr_id } }" class="block hover:bg-gray-50">
                                <div class="flex items-center px-4 py-4 sm:px-6">
                                    <div class="min-w-0 flex-1 flex items-center">
                                        <div class="flex-shrink-0">
                                            <DocumentTextIcon class="h-6 w-6 text-indigo-300 mr-4" />
                                        </div>
                                        <div class="min-w-0 flex-1 px-4 md:grid md:grid-cols-1 md:gap-4">
                                            <div>
                                                <p class="text-sm truncate flex">
                                                    <span class="font-medium">{{ upcoming.pr_headline }}</span>
                                                </p>
                                                <p class="mt-2 items-center text-sm text-gray-500 flex">
                                                    <span class="text-gray-400 mr-4">
                                                        Releases {{ ' ' }}<time :datetime="upcoming.pr_datetime">{{ formatDateRelative(upcoming.pr_datetime) }}</time>
                                                    </span>
                                                    <BuildingOfficeIcon class="flex-shrink-0 ml-3 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                                                    <span class="text-gray-400">{{ upcoming.business.name }}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <ChevronRightIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </div>
                                </div>
                            </router-link>
                        </li>
                    </ul>
                </div>
            </div>
            <div>
                <h2 class="text-2xl tracking-widest uppercase font-bold text-gray-500">New Users</h2>

                <div class="bg-white shadow overflow-hidden sm:rounded-md mt-4">
                    <ul role="list" class="divide-y divide-gray-200 font-thin">
                        <li v-for="user in users" :key="user.email">
                            <router-link :to="{ name: 'users.detail', params: { userId: user.id } }" class="block hover:bg-gray-50">
                                <div class="flex items-center px-4 py-4 sm:px-6">
                                    <div class="min-w-0 flex-1 flex items-center">
                                        <div class="flex-shrink-0">
                                            <template v-if="user.usertype === 'journalist'"><NewspaperIcon class="h-6 w-6 text-indigo-300 mr-4" /></template>
                                            <template v-else><UserIcon class="h-6 w-6 text-orange-300 mr-4" /></template>
                                        </div>
                                        <div class="min-w-0 flex-1 px-4 md:grid md:grid-cols-1 md:gap-4">
                                            <div>
                                                <p class="text-sm truncate flex">
                                                    <span class="font-medium">{{ user.profile?.name ?? 'No Name' }}</span>
                                                    <EnvelopeIcon class="flex-shrink-0 ml-3 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                                                    <span class="truncate text-gray-400">{{ user.email }}</span>
                                                </p>
                                                <p class="mt-2 items-center text-sm text-gray-500">
                                                    Registered{{ ' ' }}<time :datetime="user.created">{{ formatDateRelative(user.created) }}</time>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <ChevronRightIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </div>
                                </div>
                            </router-link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    ChevronRightIcon,
    EnvelopeIcon,
    NewspaperIcon,
    BuildingOfficeIcon,
    UserIcon,
    DocumentTextIcon
} from "@heroicons/vue/24/outline"

    export default {
        name: 'Dashboard',
        components: {
            ChevronRightIcon,
            NewspaperIcon,
            UserIcon,
            EnvelopeIcon,
            BuildingOfficeIcon,
            DocumentTextIcon,
        },
        data () {
            return {
                alerts: null,
                releases: null,
                users: null,
            }
        },
        mounted() {
            this.setMetaTitle('JournoLink Control')
            this.loadData()
        },
        methods: {
            handleSearch () {
                this.loadData();
            },
            /**
             * Load any relevant alerts
             *
             * @return {void}
             */
            loadData () {
                axios.get(route('alerts'))
                .then(response => {
                    this.alerts = response.data.alerts
                })
                .catch(e => {
                    console.dir(e)
                })

                axios.get(route('releases.upcoming'))
                .then(response => {
                    this.releases = response.data
                })
                .catch(e => {
                    console.dir(e)
                })

                axios.get(route('latestUsers'))
                .then((response) => {
                    this.users = response.data
                })
                .catch(e => {
                    console.dir(e)
                })
            },
            /**
             * load professional writing request
             * @param  professional writing id
             */
            loadProfessionalWriting (id) {
                this.$router.push({ name: 'professionalWriting.show', params: { professionalWritingId: id } })
            }
        },
        beforeRouteUpdate(to, from, next) {
            if (to && to.name === 'dashboard') {
                this.setMetaTitle('JournoLink Control')
                this.loadData()
            }

            next()
        },
    }
</script>

<style lang="less" scoped>

    .btn-danger {
        width: 100%;
        margin-top: 20px;
    }
</style>
